import React, { useEffect, useState, useRef } from 'react'
import getFullName from '@Utils/GetFullName'
import resizeImage from '@Utils/ResizeImage'
import AvatarUploader from '@Utils/AvatarUploader'
import Button from '@SiteComponents/Button'
import classnames from 'classnames'
import Error from '@Images/user_profile_error.svg'
import Pen from '@Images/edit_customization_item.svg'

import './ProfileSection.scss'

const NAME_MAX_LENGTH = 100
const LAST_NAME_MAX_LENGTH = 100

interface ProfileSectionProps {
  readonly userId: number
  readonly userName: string
  readonly userLastName: string
  readonly userEmail: string
  readonly hasLoadedSuccessfully: boolean
  readonly saving: boolean
  readonly hasUpdatedProfileSuccessfully: boolean
  readonly userAvatarUrl?: string
  readonly uploadingAvatar: boolean
  handleSaveEditedProfile(name: string, lastName: string): void
  changeAvatar(url: string): void
  setUploadingAvatar(): void
}

const ProfileSection: React.FC<ProfileSectionProps> = props => {
  const {
    userId,
    userName,
    userLastName,
    userEmail,
    hasLoadedSuccessfully,
    hasUpdatedProfileSuccessfully,
    saving,
    userAvatarUrl,
    uploadingAvatar,
  } = props

  const lastDigitOfUserId = userId % 10
  const userFullName = getFullName(userName, userLastName)
  const hasNoName = userFullName.length === 0
  const nameOrEmailForInitial = hasNoName ? userEmail : userFullName

  const [showEditProfileButton, setShowEditProfileButton] = useState(false)
  const [editingProfile, setEditingProfile] = useState(false)
  const [editedUserName, setEditedUserName] = useState(userName)
  const [editedUserLastName, setEditedUserLastName] = useState(userLastName)
  const [hasJustUpdatedProfile, setHasJustUpdatedProfile] = useState(false)
  const [errorMessageEditedUserName, setErrorMessageEditedUserName] = useState<string | null>(null)
  const [errorMessageEditedUserLastName, setErrorMessageEditedUserLastName] = useState<string | null>(null)
  const [showPen, setShowPen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const sanitizedEditedUserName = editedUserName.trim()
  const sanitizedEditedUserLastName = editedUserLastName.trim()

  useEffect(() => {
    if (hasLoadedSuccessfully) {
      setEditedUserName(userName)
      setEditedUserLastName(userLastName)
    }
  }, [hasLoadedSuccessfully])

  useEffect(() => {
    if (!saving && hasJustUpdatedProfile) {
      if (hasUpdatedProfileSuccessfully) {
        cancelEditingProfile()
      }
      setHasJustUpdatedProfile(false)
    }
  }, [saving])

  useEffect(() => {
    if (uploadingAvatar && showPen) {
      hidePen()
    }
  }, [uploadingAvatar])

  const renderPen = () => {
    if (uploadingAvatar) { return }

    setShowPen(true)
  }

  const hidePen = () => {
    setShowPen(false)
  }

  const handleOnClickAvatar = () => {
    if (uploadingAvatar) { return }

    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    const resizedFile = await resizeImage(file, 150)

    const uploader = new AvatarUploader(resizedFile, {
      bucketName: `${window.Checksub.s3Bucket}/user/${userId}/avatars`,
      uploadAvatarCallback: ({ url }) => {
        props.changeAvatar(url)
      },
      uploadingAvatarCallback: () => {
        props.setUploadingAvatar()
      }
    })

    uploader.call()
  }

  const displayEditProfileButton = () => {
    setShowEditProfileButton(true)
  }

  const hideEditProfileButton = () => {
    setShowEditProfileButton(false)
  }

  const startEditingProfile = () => {
    setEditingProfile(true)
  }

  const cancelEditingProfile = () => {
    setErrorMessageEditedUserName(null)
    setErrorMessageEditedUserLastName(null)
    setEditedUserName(userName)
    setEditedUserLastName(userLastName)
    setEditingProfile(false)
  }

  const saveEditedProfile = () => {
    if (sanitizedEditedUserName.length > NAME_MAX_LENGTH) {
      setErrorMessageEditedUserName(`Name must not exceed ${NAME_MAX_LENGTH} characters.`)
      return
    }

    if (sanitizedEditedUserLastName.length > LAST_NAME_MAX_LENGTH) {
      setErrorMessageEditedUserLastName(`Last name must not exceed ${LAST_NAME_MAX_LENGTH} characters.`)
      return
    }

    props.handleSaveEditedProfile(sanitizedEditedUserName, sanitizedEditedUserLastName)
    setHasJustUpdatedProfile(true)
  }

  const changeEditedUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (errorMessageEditedUserName) {
      setErrorMessageEditedUserName(null)
    }

    setEditedUserName(e.target.value)
  }

  const changeEditedUserLastName = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (errorMessageEditedUserLastName) {
      setErrorMessageEditedUserLastName(null)
    }

    setEditedUserLastName(e.target.value)
  }

  const renderHeaderProfileSection = () => {
    return (
      <div className="UserProfile__header">
        <div className="UserProfile__header-title">
          Personal profile
        </div>
        <div className="UserProfile__header-explanation">
          Manage all your personal details
        </div>
      </div>
    )
  }

  const renderProfileName = () => {
    return (
      <React.Fragment>
        {editingProfile ? renderProfileNameInput() : (
          <div
            className={classnames(
              'ProfileSection__name',
              { 'ProfileSection__no-name': hasNoName }
            )}
          >
            {hasNoName ? 'Edit to add name' : userFullName}
          </div>
        )}
      </React.Fragment>
    )
  }

  const renderProfileNameInput = () => {
    return (
      <div>
        <div className="ProfileSection__input-section">
          <div className="ProfileSection__input-header">
            First name:
          </div>
          <input
            className={classnames(
              'UserProfile__input',
              { 'UserProfile__input-error': !!errorMessageEditedUserName }
            )}
            defaultValue={userName}
            onChange={changeEditedUserName}
            placeholder="Enter first name"
            autoFocus
          />
          {!!errorMessageEditedUserName && renderErrorMessage(errorMessageEditedUserName)}
        </div>
        <div className="ProfileSection__input-section">
          <div className="ProfileSection__input-header">
            Last name:
          </div>
          <input
            className={classnames(
              'UserProfile__input',
              { 'UserProfile__input-error': !!errorMessageEditedUserLastName }
            )}
            defaultValue={userLastName}
            onChange={changeEditedUserLastName}
            placeholder="Enter last name"
          />
          {!!errorMessageEditedUserLastName && renderErrorMessage(errorMessageEditedUserLastName)}
        </div>
      </div>
    )
  }

  const renderProfileButtons = () => {
    return (
      <div className="UserProfile__buttons">
        {editingProfile && (
          <Button
            styleType="gray"
            size="small"
            caption="Cancel"
            onClick={cancelEditingProfile}
          />
        )}
        {editingProfile && (
          <Button
            styleType="brand"
            size="small"
            caption="Save"
            onClick={saveEditedProfile}
            disabled={saving}
          />
        )}
        {!editingProfile && showEditProfileButton && (
          <Button
            styleType="gray"
            size="small"
            caption="Edit"
            onClick={startEditingProfile}
          />
        )}
      </div>
    )
  }

  const renderErrorMessage = (message: string) => {
    return (
      <div className="UserProfile__error-message">
        <Error />
        {message}
      </div>
    )
  }

  const renderAvatar = () => {
    const isPlaceholder = !userAvatarUrl

    return (
      <div
        className={classnames(
          'ProfileSection__avatar-base',
          isPlaceholder
            ? `ProfileSection__user-avatar-placeholder ProfileSection__user-avatar-placeholder-background-${lastDigitOfUserId}`
            : 'ProfileSection__user-avatar',
          { 'is-disabled': uploadingAvatar }
        )}
        onMouseEnter={renderPen}
        onMouseLeave={hidePen}
        onClick={handleOnClickAvatar}
      >
        {isPlaceholder ? (
          showPen ? (
            <Pen />
          ) : (
            <span className="ProfileSection__user-avatar-initial">
              {nameOrEmailForInitial.charAt(0)}
            </span>
          )
        ) : (
          <React.Fragment>
            <img
              src={userAvatarUrl}
              alt="user avatar"
              className="ProfileSection__user-avatar-image"
            />
            {showPen && (
              <div className="ProfileSection__user-avatar-overlay">
                <Pen />
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    )
  }

  return (
    <div className="UserProfile__section">
      {renderHeaderProfileSection()}
      <div
        className="ProfileSection"
        onMouseEnter={displayEditProfileButton}
        onMouseLeave={hideEditProfileButton}
      >
        {renderAvatar()}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        {renderProfileName()}
        {renderProfileButtons()}
      </div>
    </div>
  )
}

export default ProfileSection
